/**
 * The screen-share publish budget — ONE decision, read by two consumers.
 *
 * `livekitCodec` turns these numbers into the encoder's `screenShareEncoding`;
 * `localMedia` turns the SAME numbers into the `getDisplayMedia` constraints.
 * They used to decide independently, and independently is how a `motion` share
 * came to be captured at 30 fps while the encoder was held to 10 by the `dense`
 * profile: the presenter's compositor built two frames out of three for an
 * encoder that dropped them on the floor.
 *
 * Kept free of any `livekit-client` import so the arithmetic below can be read,
 * and argued with, without the SDK in the way.
 */
import {
  AUDIO_SETTINGS, LAYER_SPECS, MEDIA_LIMITS, QUALITY_PROFILES, SIMULCAST_LADDER,
  type QualityProfile,
} from '@cyphex/types';
import type { ScreenShareHint } from './contract';

/** Study §1.2: a `motion` share is 1080p30 at 2 500 kbps. */
const MOTION_SHARE_BITRATE = 2_500_000;
const MOTION_SHARE_FPS = 30;

/**
 * What a screen share's SOUNDTRACK costs this participant's uplink, bps.
 *
 * 48 kbps rather than the microphone's 32: the mic budget is calibrated for
 * speech with DTX and FEC, and system audio is a music/effects signal that is
 * continuous by nature. It is still small enough to come out of the share's own
 * headroom rather than the camera's, which is the rule the rest of this file
 * exists to keep — the camera is the person, and the person never pays.
 *
 * Charged ONLY when an audio track was actually captured. `getDisplayMedia`
 * returns no audio track at all for most window and tab shares on Windows and
 * for every share on Safari, and reserving 48 kbps that nothing is going to send
 * would shave the `motion` premium off shares that are silent anyway.
 */
export const SHARE_AUDIO_BITRATE = 48_000;

/**
 * Floor for a CAPTURE request, fps.
 *
 * The `audio-only` profile publishes no video at all, so its `screenShareFps`
 * is 0 — a perfectly good encoder ceiling and a constraint no OS can satisfy.
 * Handing `getDisplayMedia` a 0 fps ceiling makes a pointless share into a
 * FAILED one, which is strictly worse, so a capture never asks for less than
 * the slowest rate any profile actually encodes at.
 */
const MIN_CAPTURE_FPS = 8;

/**
 * What the camera costs this participant's uplink while it is published, bps.
 *
 * The simulcast shape is the expensive one and therefore the one that is
 * budgeted: 150 + 500 + 1 700 = 2 350 kbps for the three `SIMULCAST_LADDER`
 * rungs `publishDefaults` sends. The SVC shape is cheaper even at ITS worst —
 * one 1 700 kbps stream plus the 360p VP8 backup LiveKit publishes for
 * subscribers that cannot decode AV1, 2 200 kbps — so this single
 * ladder-shaped number bounds both codecs, and there is no branch here to drift
 * out of step with the codec choice made in `livekitCodec`.
 *
 * Dynacast pauses whichever rungs nobody is watching, so this is a ceiling and
 * not a bill. It has to be: the moment a subscriber asks for the top rung is
 * not ours to schedule, and a ceiling that only holds while nobody is watching
 * is not a ceiling.
 */
export const cameraPublishBitrate = (): number =>
  SIMULCAST_LADDER.reduce((total, layer) => total + LAYER_SPECS[layer].bitrate, 0);

/**
 * The share's encoder ceiling, bps, given what the camera is already sending.
 *
 * THE ARITHMETIC. `MEDIA_LIMITS.MAX_PUBLISH_BITRATE` is 4 000 kbps per
 * participant and, until this function existed, nothing anywhere enforced it:
 * livekit.yaml's `limit.bytes_per_sec` is node-wide, not per-participant, so
 * the server cannot express this cap at all. That was harmless only while the
 * `motion` branch was unreachable and every share ran at the profile's
 * 1 500 kbps. Now that the picker's hint reaches the encoder, a presenter with
 * their camera on would publish
 *
 *     camera 2 350 + Opus 32 + share 2 500 = 4 882 kbps    (22 % over)
 *
 * so the SHARE absorbs the difference and the camera never does — the camera is
 * the person, and a meeting that softens the face to sharpen the slide has its
 * priorities backwards. Headroom is therefore 4 000 - 2 350 - 32 = 1 618 kbps
 * with a camera live, and the full 2 500 kbps with it off, which is the case
 * `motion` was written for anyway.
 *
 * The profile's own `screenShareBitrate` is the FLOOR, and it always fits: the
 * worst of them is standard's 1 500 kbps, 118 kbps inside the headroom above.
 * So this clamp can only ever shave the `motion` premium, and the default
 * `detail` path comes out bit-for-bit what it was before the budget existed.
 */
/**
 * The least a screen share may be published at and still be a picture.
 *
 * 200 kbps at the 1080p cap is coarse, but it is legible for a static slide and
 * it is unambiguously better than the zero this replaces.
 */
const MIN_SHARE_BITRATE = 200_000;

export function shareBitrate(
  profile: QualityProfile,
  hint: ScreenShareHint,
  cameraBitrate: number,
  /** `SHARE_AUDIO_BITRATE` when the share carries sound, 0 when it does not. */
  shareAudioBitrate = 0,
): number {
  /*
   * A SHARE THAT PUBLISHES 0 bps IS WORSE THAN NO SHARE.
   *
   * `audio-only` sets `screenShareBitrate` to 0 — sensible as a statement that
   * this profile does not carry video, but ruinous as an encoder ceiling. The
   * SDK does not treat `{maxBitrate: 0}` as "unset": it is a truthy object, so
   * it skips the default-encoding fallback and hands the zero straight to
   * `setParameters`. The track publishes, the roster shows somebody presenting,
   * and not one frame ever arrives. A silent, total failure with a green light
   * on it.
   *
   * So the floor is the lowest rate that can still carry a picture, and a
   * profile that wants no video must refuse the share OUTRIGHT rather than
   * publish an invisible one.
   */
  const profileFloor = QUALITY_PROFILES[profile].screenShareBitrate;
  const floor = profileFloor > 0 ? profileFloor : MIN_SHARE_BITRATE;
  const wanted = hint === 'motion' && profile === 'standard' ? MOTION_SHARE_BITRATE : floor;
  const headroom =
    MEDIA_LIMITS.MAX_PUBLISH_BITRATE - cameraBitrate - AUDIO_SETTINGS.maxBitrate - shareAudioBitrate;
  return Math.max(floor, Math.min(wanted, headroom));
}

/**
 * The share's frame rate, fps — the encoder ceiling AND the capture request.
 *
 * `motion` buys 30 fps only under the profile that can pay for it. `dense`
 * holds the encoder to 10 and `low-bandwidth` to 8, and those two are exactly
 * the sessions where a presenter's machine has least to spare, so asking their
 * compositor for 30 is the worst possible moment to ask.
 */
export function shareFps(profile: QualityProfile, hint: ScreenShareHint): number {
  if (hint === 'motion' && profile === 'standard') return MOTION_SHARE_FPS;
  /* Same reasoning as the bitrate floor: 0 fps is not "no ceiling", it is a
     ceiling of nothing, and it reaches the encoder verbatim. */
  const profileFps = QUALITY_PROFILES[profile].screenShareFps;
  return profileFps > 0 ? profileFps : MIN_CAPTURE_FPS;
}

/** `shareFps`, floored so it is always a rate a display can be captured at. */
export const shareCaptureFps = (profile: QualityProfile, hint: ScreenShareHint): number =>
  Math.max(MIN_CAPTURE_FPS, shareFps(profile, hint));
